// MfaVerifyPanel — answers a two-step verification challenge (task 387.11.6, ADR-063).
//
// Shared by sign-in (/sign-in/verify, CHALLENGE) and the tenant-switch step-up: the caller supplies what "verify"
// means. A person may hold several factors — an authenticator app per device, passkeys, an email method — and picks
// one; passkeys are one choice (the browser offers whichever this device holds). A recovery code is the way out
// when no factor is at hand.

import React, { useMemo, useState } from 'react';
import { Alert, Button, Flex, Form, Input, Radio, Space, Typography } from 'antd';
import type { MfaStep } from '@/api/contracts/session';
import type { MfaAnswer } from '@/api/endpoints/mfa';
import { RECOVERY_KEY, verificationChoices } from './verificationChoices';
import { normalizeError } from '@/shared/utils/errorHandler';
import { passkeyErrorMessage, passkeysSupported, signWithPasskey } from './passkeys';

export interface MfaVerifyActions {
  verify: (answer: MfaAnswer) => Promise<void>;
  sendEmailCode: (factorId: string) => Promise<{ sentTo: string }>;
  passkeyOptions: () => Promise<{ optionsJson: string }>;
}

interface Props {
  step: MfaStep;
  actions: MfaVerifyActions;
}

function MfaVerifyPanel({ step, actions }: Props): React.ReactElement {
  const choices = useMemo(() => verificationChoices(step.factors), [step.factors]);
  const [selected, setSelected] = useState<string>(choices[0]?.key ?? RECOVERY_KEY);
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choice = choices.find((c) => c.key === selected);
  const isRecovery = selected === RECOVERY_KEY;

  const select = (key: string): void => {
    setSelected(key);
    setCode('');
    setSentTo(null);
    setError(null);
  };

  const run = async (work: () => Promise<void>, passkey = false): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(
        passkey && !isHttpError(err)
          ? passkeyErrorMessage(err)
          : (await normalizeError(err)).message,
      );
    } finally {
      setBusy(false);
    }
  };

  const submitCode = (): Promise<void> =>
    run(() =>
      isRecovery
        ? actions.verify({ recoveryCode: code.trim() })
        : actions.verify({ factorId: choice?.factorId, code: code.trim() }),
    );

  const sendEmail = (): Promise<void> =>
    run(async () => {
      if (!choice?.factorId) return;
      const sent = await actions.sendEmailCode(choice.factorId);
      setSentTo(sent.sentTo);
    });

  const answerWithPasskey = (): Promise<void> =>
    run(async () => {
      const { optionsJson } = await actions.passkeyOptions();
      const passkeyResponse = await signWithPasskey(optionsJson);
      await actions.verify({ passkeyResponse });
    }, true);

  const codeForm = (label: string, placeholder: string): React.ReactElement => (
    <Form layout="vertical" onFinish={() => void submitCode()}>
      <Form.Item label={label} style={{ marginBottom: 12 }}>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="one-time-code"
          inputMode={isRecovery ? 'text' : 'numeric'}
          placeholder={placeholder}
          aria-label={label}
          autoFocus
        />
      </Form.Item>
      <Button
        type="primary"
        htmlType="submit"
        block
        loading={busy}
        disabled={code.trim().length === 0}
      >
        Verify
      </Button>
    </Form>
  );

  return (
    <Flex vertical gap={16}>
      {choices.length > 1 && !isRecovery && (
        <Radio.Group value={selected} onChange={(e) => select(e.target.value as string)}>
          <Space direction="vertical">
            {choices.map((c) => (
              <Radio key={c.key} value={c.key}>
                {c.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}

      {error && <Alert type="error" showIcon message={error} />}

      {isRecovery && codeForm('Recovery code', 'XXXXX-XXXXX')}

      {choice?.method === 'TOTP' && codeForm('Code from your authenticator app', '6-digit code')}

      {choice?.method === 'EMAIL_OTP' &&
        (sentTo ? (
          <>
            <Typography.Text type="secondary">We sent a code to {sentTo}.</Typography.Text>
            {codeForm('Code from the email', '6-digit code')}
            <Button type="link" onClick={() => void sendEmail()} disabled={busy}>
              Send another code
            </Button>
          </>
        ) : (
          <Button type="primary" block loading={busy} onClick={() => void sendEmail()}>
            Email me a code
          </Button>
        ))}

      {choice?.method === 'WEBAUTHN' &&
        (passkeysSupported() ? (
          <Button type="primary" block loading={busy} onClick={() => void answerWithPasskey()}>
            Use a passkey
          </Button>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="This browser cannot use passkeys. Choose another method or a recovery code."
          />
        ))}

      {step.recoveryCodesAvailable &&
        (isRecovery ? (
          choices.length > 0 && (
            <Button type="link" onClick={() => select(choices[0].key)}>
              Use another method
            </Button>
          )
        ) : (
          <Button type="link" onClick={() => select(RECOVERY_KEY)}>
            Use a recovery code
          </Button>
        ))}
    </Flex>
  );
}

function isHttpError(error: unknown): boolean {
  return error instanceof Error && error.name === 'HTTPError';
}

export default MfaVerifyPanel;
